export interface PaymentRail {
  preferred_provider: 'stripe' | 'paystack' | 'flutterwave';
  use_paystack: boolean;
  use_flutterwave?: boolean;
  use_stripe: boolean;
  stripe_publishable_key?: string | null;
  stripe_region?: 'us' | 'eu' | null;
  charge_currency?: string;
  processor_fee_profile?: string;
  paystack_currency?: string | null;
  flutterwave_currency?: string | null;
  country_code?: string | null;
}

export interface CheckoutPurchaseData {
  provider?: 'stripe' | 'paystack' | 'flutterwave';
  clientSecret?: string;
  paymentIntentId?: string;
  saleId?: string;
  authorization_url?: string;
  access_code?: string;
  reference?: string;
  tx_ref?: string;
  stripe_publishable_key?: string;
  stripe_region?: string;
  payment_rail?: PaymentRail;
}

export function isHostedCheckoutRail(rail?: PaymentRail | null): boolean {
  return Boolean(rail?.use_paystack || rail?.use_flutterwave);
}

export function extractPaystackReferenceFromUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    return (
      parsed.searchParams.get('reference') ||
      parsed.searchParams.get('trxref') ||
      parsed.searchParams.get('ref')
    );
  } catch {
    const match = url.match(/[?&](?:reference|trxref)=([^&]+)/i);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  }
}

export function extractFlutterwaveReturnFromUrl(url: string): {
  transaction_id?: string;
  tx_ref?: string;
  reference?: string;
} | null {
  try {
    const parsed = new URL(url);
    const transaction_id = parsed.searchParams.get('transaction_id')?.trim() || undefined;
    const tx_ref =
      parsed.searchParams.get('tx_ref')?.trim() ||
      parsed.searchParams.get('txRef')?.trim() ||
      parsed.searchParams.get('reference')?.trim() ||
      undefined;
    if (!transaction_id && !tx_ref) return null;
    return { transaction_id, tx_ref, reference: tx_ref };
  } catch {
    const txMatch = url.match(/[?&](?:tx_ref|txRef|reference)=([^&]+)/i);
    const idMatch = url.match(/[?&]transaction_id=([^&]+)/i);
    if (!txMatch && !idMatch) return null;
    const tx_ref = txMatch?.[1] ? decodeURIComponent(txMatch[1]) : undefined;
    const transaction_id = idMatch?.[1] ? decodeURIComponent(idMatch[1]) : undefined;
    return { transaction_id, tx_ref, reference: tx_ref };
  }
}

/** Buyer charge currency from rail (lowercase ISO). */
export function resolveChargeCurrency(
  eventCurrency: string,
  paymentRail?: PaymentRail | null,
): string {
  if (paymentRail?.charge_currency) return paymentRail.charge_currency.toLowerCase();
  if (paymentRail?.use_paystack && paymentRail.paystack_currency) {
    return paymentRail.paystack_currency.toLowerCase();
  }
  if (paymentRail?.use_flutterwave && paymentRail.flutterwave_currency) {
    return paymentRail.flutterwave_currency.toLowerCase();
  }
  return (eventCurrency || 'usd').toLowerCase();
}

export function getSalesPaymentMethod(provider?: string | null): { token: string } {
  if (provider === 'paystack') return { token: 'paystack' };
  if (provider === 'flutterwave') return { token: 'flutterwave' };
  return { token: 'stripe_payment_intent' };
}

/** Minimal tier fields persisted before hosted redirect (page reload clears in-memory cart). */
export interface PaystackCartSnapshotLine {
  tierId: string;
  quantity: number;
  tier?: {
    _id: string;
    fee_name: string;
    fee: number;
    currency?: string;
    description?: string;
    sponsor?: unknown;
    sponsor_discount?: unknown;
    sponsur_free_tier?: boolean;
  };
}

export interface PaystackWidgetSessionContext {
  widgetId?: string;
  eventId?: string;
  chargeCurrency?: string;
  contact?: { email?: string; name?: string; phone?: string };
  feeBreakdown?: unknown;
  feeTotal?: number;
  refCode?: string;
  viewCorrelationId?: string;
  sponsorAttributions?: Array<{ event_sponsor_id: string }>;
  sponsorLeadConsent?: { event_sponsor_id: string; interested: boolean };
  selectedOccurrenceIso?: string | null;
  cartTierIds?: string[];
  cartQuantities?: number[];
  cartSnapshot?: PaystackCartSnapshotLine[];
  selectedTables?: unknown[];
  selectedSeats?: unknown[];
  holdId?: string | null;
  unlockedSponsorTiers?: Record<string, { value: number; type: string; code: string }>;
  discount?: unknown;
  provider?: 'paystack' | 'flutterwave';
}

type CartLike = { tier: { _id: string }; quantity: number };

/** Rebuild cart after hosted redirect when widget state was reset. */
export function rebuildCartForPaystackReturn<T extends CartLike>(
  liveCart: T[],
  ctx: PaystackWidgetSessionContext | null | undefined,
  feeStructures: Array<{ _id: string } & T['tier']>,
): T[] {
  if (liveCart.length > 0) return liveCart;

  if (ctx?.cartSnapshot?.length) {
    return ctx.cartSnapshot
      .map(line => {
        const tier =
          feeStructures.find(f => f._id === line.tierId) ??
          (line.tier as (typeof feeStructures)[number] | undefined);
        if (!tier?._id) return null;
        return { tier, quantity: line.quantity } as T;
      })
      .filter((row): row is T => row != null);
  }

  if (ctx?.cartTierIds?.length) {
    return ctx.cartTierIds
      .map((tierId, i) => {
        const tier = feeStructures.find(f => f._id === tierId);
        if (!tier) return null;
        return { tier, quantity: ctx.cartQuantities?.[i] ?? 1 } as T;
      })
      .filter((row): row is T => row != null);
  }

  return [];
}
